import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { LoadingSpinner } from '@uhg-abyss/web/ui/LoadingSpinner';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

import { PatientReviewDetailsSection } from '../../../../../common/ProviderDetailTabs/About/PatientReviews/PatientReviewDetailsSection';
import { usePreventScrollPropagation } from '../../../../../common/Utils/a11yUtils/a11yUtils';
import { StoreKeys } from '../../../../../hooks/useStore/state';
import { useStore } from '../../../../../hooks/useStore/useStore';
import {
  HealthGradesBulkInfo,
  Survey,
} from '../../../../../models/Healthgrades';
import { useGetPatientCardLength } from '../../../../../utils/providerDetails.utils';
import { widgetModalEvent } from '../../../../../utils/widgetEvents/widgetModalEvent';
import { Constants } from '../../../../Constants';
import { ContentWrapper } from '../../../../ContentWrapper';
import { PatientReviewMobileCarousel } from '../../../../ProviderDetailTabs/About/PatientReviews/PatientReviewMobileCarousel';
import { PatientReviewsModal } from '../../../../ProviderDetailTabs/About/PatientReviews/PatientReviewModal';
import {
  RatingSectionContainerStyles,
  StyledIcon,
  modalStyles,
  verbatimContainerStyles,
} from '../../../../ProviderDetailTabs/About/PatientReviews/PatientReviews.styled';
import { PatientReviewsViewAll } from '../../../../ProviderDetailTabs/About/PatientReviews/PatientReviewsViewAll';
import { PatientVerbatimCard } from '../../../../ProviderDetailTabs/About/PatientReviews/PatientVerbatimCard';
import {
  AboutHeading,
  ReviewsContainer,
  ReviewsContentContainer,
} from './About.styled';
import { PatientReviewsErrorHandlingWidget } from './PatientReviewsErrorHandlingWidget';

type Props = {
  healthgradesReview: HealthGradesBulkInfo;
  isLoading: boolean;
  providerName: string;
  primaryDegree?: string;
  isReviewError: boolean;
  setViewAllReviews(val: boolean): void;
};
export const PatientReviews = ({
  healthgradesReview,
  isLoading,
  providerName,
  primaryDegree,
  setViewAllReviews,
  isReviewError,
}: Props) => {
  const isWidget = useStore(StoreKeys.IS_WIDGET);
  const [reviewModal, setReviewModal] = useState<Survey | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  usePreventScrollPropagation(isModalOpen);

  const reviewModalHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const reviewsLength = healthgradesReview?.surveys?.length || 0;
  const displayCarousel = useGetPatientCardLength(reviewsLength, isWidget);
  const displayViewAll = reviewsLength > 4;
  const handleViewAllOnClick = () => {
    widgetModalEvent({ pageName: 'reviews view all' });
    setViewAllReviews(true);
  };

  useEffect(() => {
    if (reviewModal) {
      setIsModalOpen(true);
    }
  }, [reviewModal]);

  useEffect(() => {
    // Using setTimeout to ensure the modal is focused after it opens and a11y trr is updated before applying focus
    // so that screen reader reads hidden text before other elements
    if (isModalOpen) {
      setTimeout(() => {
        reviewModalHeadingRef.current?.focus();
        reviewModalHeadingRef.current?.removeAttribute('role');
      }, 0);
    }
  }, [isModalOpen]);

  const noReview = () =>
    !isReviewError ? (
      <Text>
        {t('PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION.NO_REVIEWS_FOUND')}
      </Text>
    ) : (
      <PatientReviewsErrorHandlingWidget />
    );
  return (
    <ReviewsContainer>
      <ContentWrapper isWidget={isWidget}>
        <ReviewsContentContainer data-testid="patient-review-section">
          <ModalDialog
            closeOnClickOutside={false}
            css={modalStyles}
            data-testid="provider-details-patient-reviews"
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setReviewModal(null);
            }}
            ref={reviewModalHeadingRef}
            scrollableFocus={true}
            /* Title is added to prevent JS error as the component required title prop, title is hidden adding css rules for abyss-modal-header-container */
            title="Patient Reviews"
          >
            <div>
              <PatientReviewsModal review={reviewModal} />
            </div>
          </ModalDialog>
          {!isModalOpen && (
            <>
              <StyledIcon
                icon="healthgrades"
                isScreenReadable
                size={132}
                title={t('HEALTH_GRADES_LOGO_TITLE')}
              />
              <AboutHeading
                data-auto-testid="patient-review-heading"
                data-testid="patient-review-heading"
                level={3}
                size="sm"
              >
                {`${t(
                  'PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION.WHAT_PATIENTS_ARE_SAYING_ABOUT'
                )} ${providerName}${primaryDegree ? `, ${primaryDegree}` : ''}`}
              </AboutHeading>
              {isLoading ? (
                <LoadingSpinner
                  ariaLoadingLabel="loading results"
                  isLoading={isLoading}
                  size="xs"
                />
              ) : (
                <div
                  data-auto-testid="reviews-container"
                  data-testid="reviews-container"
                  style={RatingSectionContainerStyles}
                >
                  {healthgradesReview ? (
                    <div>
                      <PatientReviewDetailsSection
                        healthgradesReview={healthgradesReview}
                      />
                      {healthgradesReview?.surveys?.length ? (
                        <>
                          <Flex
                            alignContent="space-between"
                            alignItems="baseline"
                            css={{ gap: '16px', marginTop: '10px' }}
                            justify="start"
                          >
                            <Heading
                              css={{
                                '&.abyss-heading-root': {
                                  height: '20px !important',
                                  fontSize: '16px !important',
                                  lineHeight: '20px !important',
                                },
                              }}
                              data-auto-testid="what-patients-are-saying-subtitle"
                              data-testid="what-patients-are-saying-subtitle"
                              level={4}
                              size="xs"
                            >
                              {t(
                                'PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION.WHAT_PATIENTS_ARE_SAYING'
                              )}
                            </Heading>
                            <PatientReviewsViewAll
                              displayViewAll={displayViewAll}
                              handleViewAllOnClick={handleViewAllOnClick}
                              locationForAnalytics={`body:${Constants.PROVIDER_DETAILS.ABOUT_TAB.REVIEW_SECTION.WHAT_PATIENTS_ARE_SAYING}`}
                            />
                          </Flex>
                          {displayCarousel ? (
                            <PatientReviewMobileCarousel
                              healthgradesReview={healthgradesReview}
                              setIsModalOpen={setIsModalOpen}
                              setReviewModal={setReviewModal}
                            />
                          ) : (
                            <Flex
                              alignItems="flex-end"
                              css={verbatimContainerStyles}
                            >
                              {healthgradesReview.surveys
                                .slice(0, 4)
                                .map((survey) => (
                                  <PatientVerbatimCard
                                    key={survey.id}
                                    setIsModalOpen={setIsModalOpen}
                                    setReviewModal={setReviewModal}
                                    survey={survey}
                                  />
                                ))}
                            </Flex>
                          )}
                        </>
                      ) : null}
                    </div>
                  ) : (
                    noReview()
                  )}
                </div>
              )}
            </>
          )}
        </ReviewsContentContainer>
      </ContentWrapper>
    </ReviewsContainer>
  );
};
